import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiServer, FiCloud, FiCode, FiTool, FiAward, FiStar, FiCheck, FiUsers, FiCpu, FiPlay } = FiIcons;

const Skills = ({ viewMode }) => {
  const [activeCategory, setActiveCategory] = useState('security');

  const skillCategories = {
    security: {
      title: "Security & Compliance",
      icon: FiShield,
      color: "from-red-600 to-red-700",
      skills: [
        { name: "M365 Security & Compliance", level: 98, years: "10+" },
        { name: "SOC 2 Type II Readiness", level: 95, years: "4+" },
        { name: "NIST & GDPR Frameworks", level: 92, years: "8+" },
        { name: "DLP & Threat Protection", level: 94, years: "12+" },
        { name: "PCI-DSS Compliance", level: 90, years: "10+" },
        { name: "Incident Response Lead", level: 93, years: "15+" }
      ]
    },
    cloud: {
      title: "Microsoft Ecosystem",
      icon: FiCloud,
      color: "from-blue-500 to-blue-600",
      skills: [
        { name: "Azure AD / Entra ID", level: 96, years: "12+" },
        { name: "Microsoft Teams Architecture", level: 97, years: "8+" },
        { name: "SharePoint & OneDrive Admin", level: 95, years: "15+" },
        { name: "M365 Tenant Migrations", level: 94, years: "8+" },
        { name: "InTune & Endpoint Manager", level: 88, years: "6+" },
        { name: "SaaS Security Governance", level: 92, years: "10+" }
      ]
    },
    infrastructure: {
      title: "Infrastructure & Networking",
      icon: FiServer,
      color: "from-teal-500 to-teal-600",
      skills: [
        { name: "Hyper-V Clustered Environments", level: 95, years: "15+" },
        { name: "Cisco Networking & Firewalls", level: 92, years: "20+" },
        { name: "Hybrid Cloud Connectivity", level: 94, years: "10+" },
        { name: "VPN & Secure Remote Access", level: 93, years: "20+" },
        { name: "Backup & Disaster Recovery", level: 96, years: "20+" },
        { name: "Active Directory (Hybrid)", level: 97, years: "20+" }
      ]
    },
    ai: {
      title: "AI & Automation",
      icon: FiCpu,
      color: "from-purple-600 to-purple-700",
      skills: [
        { name: "n8n Workflow Automation", level: 90, years: "3+" },
        { name: "AI-Powered SaaS Development", level: 88, years: "2+" },
        { name: "Supabase Backend Architecture", level: 85, years: "2+" },
        { name: "LLM Integration & Prompt Engineering", level: 87, years: "2+" },
        { name: "AI Security & Governance", level: 82, years: "2+" },
        { name: "Custom AI Agent Development", level: 86, years: "2+" }
      ]
    },
    leadership: {
      title: "IT Operations & Strategy",
      icon: FiUsers,
      color: "from-orange-600 to-orange-700",
      skills: [
        { name: "IT Department Management", level: 95, years: "15+" },
        { name: "Vendor & Budget Management", level: 92, years: "15+" },
        { name: "Technical Documentation (C-Suite)", level: 98, years: "20+" },
        { name: "Project Management", level: 94, years: "15+" },
        { name: "AI Strategy & Implementation", level: 85, years: "3+" },
        { name: "User Training & Support", level: 95, years: "25+" }
      ]
    }
  };

  const certifications = [
    { name: "SOC 2 Compliance Lead", issuer: "Internal/Industry", year: "2023", status: "Active" },
    { name: "Cyber-Security Technologist", issuer: "CeriFi", year: "2022", status: "Specialist" },
    { name: "M365 Security Administrator", issuer: "Microsoft", year: "2021", status: "Active" },
    { name: "PCI-DSS Compliance Officer", issuer: "Four Seasons", year: "2015", status: "Expert" },
    { name: "Hyper-V Specialist", issuer: "Microsoft", year: "2014", status: "Advanced" },
    { name: "Cisco Networking Professional", issuer: "Cisco", year: "2010", status: "Legacy Expert" },
    { name: "Network Policy Server Expert", issuer: "Verus", year: "2018", status: "Active" },
    { name: "USAF Technical Training", issuer: "United States Air Force", year: "1995", status: "Veteran" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
            Technical Arsenal
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            {viewMode === 'professional'
              ? "Two decades of engineering excellence, from Air Force systems to modern M365 Zero-Trust architectures and AI automation."
              : "The toolkit that lets me secure enterprises by day and build AI automations by night."}
          </p>
        </motion.div>
      </section>

      {/* Featured YouTube Video Section */}
      <section className="py-12 -mt-8 relative z-10">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-600 to-blue-700 rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 md:p-12">
              <div className="flex flex-col justify-center text-white">
                <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6 w-fit">
                  <SafeIcon icon={FiCpu} className="w-4 h-4" />
                  <span>Featured Content</span>
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                  AI Automation in Action
                </h2>
                <p className="text-lg text-purple-100 mb-6 leading-relaxed">
                  Watch how I leverage AI, n8n workflows, and Supabase to build intelligent automation systems that transform business operations. This video showcases my hands-on approach to solving real-world problems with cutting-edge AI technology.
                </p>
                <div className="flex flex-wrap gap-3">
                  {['n8n Workflows', 'AI Integration', 'Supabase', 'Automation'].map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-sm font-medium border border-white/20"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-2xl bg-black/20 backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <a
                  href="https://youtu.be/pr5BpPYJmVA?si=H97aqaanU6AoejAS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative group"
                >
                  <div className="aspect-video bg-gradient-to-br from-purple-900 to-blue-900">
                    <iframe
                      src="https://www.youtube.com/embed/pr5BpPYJmVA"
                      title="Gary Fagerholm - AI Automation Showcase"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <SafeIcon icon={FiPlay} className="w-10 h-10 text-purple-600 ml-1" />
                    </div>
                  </div>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills Categories */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.entries(skillCategories).map(([key, category]) => (
              <motion.button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeCategory === key
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={category.icon} className="w-5 h-5 mr-2" />
                {category.title}
              </motion.button>
            ))}
          </div>

          {/* Active Category Skills */}
          <motion.div
            key={activeCategory}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-4xl mx-auto"
          >
            <div className={`bg-gradient-to-r ${skillCategories[activeCategory].color} rounded-2xl p-8 mb-8 text-white shadow-xl`}>
              <div className="flex items-center mb-4">
                <SafeIcon icon={skillCategories[activeCategory].icon} className="w-8 h-8 mr-4" />
                <h2 className="font-heading text-3xl font-bold">
                  {skillCategories[activeCategory].title}
                </h2>
              </div>
              <p className="text-lg opacity-90">
                {activeCategory === 'ai'
                  ? "Pioneering AI-driven automation solutions with n8n, Supabase, and custom LLM integrations to revolutionize business workflows."
                  : `Extensive hands-on experience managing complex ${skillCategories[activeCategory].title.toLowerCase()} for finance, hospitality, and legal sectors.`}
              </p>
            </div>

            <div className="grid gap-6">
              {skillCategories[activeCategory].skills.map((skill, index) => (
                <motion.div
                  key={skill.name}
                  variants={itemVariants}
                  className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg text-gray-900">{skill.name}</h3>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {skill.years} Experience
                      </span>
                      <span className="text-sm font-bold text-teal-600">
                        {skill.level}%
                      </span>
                    </div>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <motion.div
                        className={`bg-gradient-to-r ${skillCategories[activeCategory].color} h-3 rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications & Milestones */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SafeIcon icon={FiAward} className="w-16 h-16 mx-auto mb-6 text-teal-600" />
            <h2 className="font-heading text-4xl font-bold mb-6 text-gray-900">Professional Milestones</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A record of continuous growth and technical validation across three decades of IT evolution.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300 border-t-4 border-teal-500"
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiAward} className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{cert.name}</h3>
                <p className="text-sm text-gray-600 mb-1">{cert.issuer}</p>
                <div className="flex items-center justify-center mt-3">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold uppercase tracking-wider">
                    {cert.status}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-gradient-to-r from-teal-700 to-blue-900 text-white">
        <motion.div
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SafeIcon icon={FiCpu} className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Security Through Automation</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            I believe the future of IT security lies in intelligent automation. By leveraging AI and streamlined M365 controls, we can build environments that are not only secure but also efficient and resilient.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: "M365 First", desc: "Maximizing native security tools for seamless compliance." },
              { title: "Zero Trust", desc: "Implementing identity-driven security at every layer." },
              { title: "AI Enhanced", desc: "Using automation to triage threats before they escalate." }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <h3 className="font-heading text-xl font-bold mb-3 text-yellow-400">{item.title}</h3>
                <p className="text-teal-50 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Skills;